import get from 'lodash/get';
import { useEffect, useContext, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import Button from 'components/common/Button';
import ErrorMessage from 'components/common/ErrorMessage';
import Text from 'components/common/Text';
import AdultsKidsSelector from 'components/signUpFunnel/boxRecommender/AdultKidsSelector';
import { ProgressBarContext } from 'components/signUpFunnel/Contexts';
import BoxQuestionsToggles from 'components/signUpFunnel/shared/BoxQuestionsToggles';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import { BrandContext } from 'context/BrandContext';
import { getRoutesMap } from 'helpers/routes';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useBoxRecommender from 'hooks/useBoxRecommender';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import { FAMILY, TEMPO, BOX_QUESTIONS, BOX_RECOMMENDER_PATHS } from 'lib/constants';
import RequestService from 'services/RequestService';
import { pluralizeWithoutCount } from 'utilities/strings';

const BoxRecommenderContainer = () => {
  const { setProgress } = useContext(ProgressBarContext);
  const { brand } = useContext(BrandContext);
  const errorRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const mealPlanSlug = useFunnelMealPlan();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;
  const isTempoMealPlan = mealPlanSlug === TEMPO;
  const routesMap = getRoutesMap(brand, isFamilyMealPlan);

  const { pathname } = location;
  const boxRecommenderPath = pathname.split('/')[2];
  const isBoxPeopleStep = boxRecommenderPath === BOX_RECOMMENDER_PATHS.BOX_PEOPLE;
  const isBoxMealsStep = boxRecommenderPath === BOX_RECOMMENDER_PATHS.BOX_MEALS;
  const isBoxDaysStep = boxRecommenderPath === BOX_RECOMMENDER_PATHS.BOX_DAYS;

  const routesAndAnalyticsMap = {
    [routesMap.onBoxRecommenderPeopleStep]: {
      nextRoute: routesMap.onBoxRecommenderMealsStep,
      amplitudePageViewed: isFamilyMealPlan ? 'boxPeopleViewedFamily' : 'boxPeopleViewed',
      amplitudePageSubmitted: isFamilyMealPlan ? 'boxPeopleSubmittedFamily' : 'boxPeopleSubmitted',
      percent: '45',
    },
    [routesMap.onBoxRecommenderMealsStep]: {
      nextRoute: routesMap.onBoxRecommenderDaysStep,
      amplitudePageViewed: isFamilyMealPlan ? 'boxMealsViewedFamily' : 'boxMealsViewed',
      amplitudePageSubmitted: isFamilyMealPlan ? 'boxMealsSubmittedFamily' : 'boxMealsSubmitted',
      percent: '50',
    },
    [routesMap.onBoxRecommenderDaysStep]: {
      nextRoute: routesMap.onBuildABoxStep,
      amplitudePageViewed: isFamilyMealPlan ? 'boxDaysViewedFamily' : 'boxDaysViewed',
      amplitudePageSubmitted: isFamilyMealPlan ? 'boxDaysSubmittedFamily' : 'boxDaysSubmitted',
      percent: '55',
    },
  };

  const {
    boxSignupData,
    error,
    setError,
    isSubmitting,
    handleBoxSignupAnswerClick,
    handleContinueClick,
    setIsSubmitting,
    icons,
    setBoxSignupData,
  } = useBoxRecommender({
    boxRecommenderPath,
    mealPlanSlug,
    onNavigate: () => {},
    location: routesAndAnalyticsMap[location.pathname],
  });

  const onNavigate = () => {
    const nextRoute = routesAndAnalyticsMap[location.pathname].nextRoute;
    navigate(nextRoute);
  };

  const handleContinue = async () => {
    const success = await handleContinueClick();
    if (success) onNavigate();
  };

  useEffect(() => {
    const currentRouteData = routesAndAnalyticsMap[location.pathname];
    setProgress({ percent: currentRouteData?.percent });
  }, [location.pathname]);

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (brand) {
        const pageViewed = routesAndAnalyticsMap[location.pathname].amplitudePageViewed;
        if (pageViewed) {
          logEvent(events.signUp[brand].boxRecommender[pageViewed]);
        }
      }
    },
    [brand, location.pathname, routesAndAnalyticsMap, isFamilyMealPlan]
  );

  const handleAdultsKidsSelection = useAnalyticsCallback(
    async ({ adults, kids }) => {
      const adultText = pluralizeWithoutCount(adults, 'adult');
      const kidText = pluralizeWithoutCount(kids, 'kid');
      const expectedName = `${adults} ${adultText} + ${kids} ${kidText}`;

      const matchingAnswer = boxSignupData.answers.find(answer => answer.name === expectedName);

      if (matchingAnswer) {
        setError(null);
        setIsSubmitting(true);

        const newAnswers = boxSignupData.answers.map(a => {
          if (a.name === expectedName) {
            return { ...a, selected: true };
          }
          return { ...a, selected: false };
        });
        setBoxSignupData({ ...boxSignupData, answers: newAnswers });

        const response = await RequestService.updateBoxSignupAnswerSelections({
          params: {
            boxRecommendation: { answerIds: [matchingAnswer.id], pathname: boxRecommenderPath },
          },
        });
        if (response.ok) {
          const nextRoute = routesAndAnalyticsMap[location.pathname].nextRoute;
          navigate(nextRoute);
          setIsSubmitting(false);
        } else {
          setError(response.error);
          setIsSubmitting(false);
        }
      } else {
        setError('Something went wrong. Please try again or contact support.');
      }
    },
    ({ events, logEvent }) => {
      const pageSubmitted = routesAndAnalyticsMap[location.pathname].amplitudePageSubmitted;
      const selectedAnswers = boxSignupData.answers
        .filter(a => a.selected)
        .map(a => a.name)
        .join(', ');
      logEvent(events.signUp[brand].boxRecommender[pageSubmitted], {
        mealPlanSlug,
        softballQuestionsInputs: selectedAnswers,
      });
    },
    [brand, boxSignupData, mealPlanSlug, location.pathname, routesAndAnalyticsMap]
  );

  const getInitialAdults = () => {
    if (!boxSignupData.answers) return 2;
    const selectedAnswer = boxSignupData.answers.find(a => a.selected);
    if (selectedAnswer && selectedAnswer.name) {
      // Parse adults from the name like "2 adults + 0 kids"
      const match = selectedAnswer.name.match(/(\d+) adults?/);
      if (match) {
        const adultsCount = parseInt(match[1], 10);
        return adultsCount;
      }
    }
    return 2;
  };

  const getInitialKids = () => {
    if (!boxSignupData.answers) return isFamilyMealPlan ? 2 : 0;
    const selectedAnswer = boxSignupData.answers.find(a => a.selected);
    if (selectedAnswer && selectedAnswer.name) {
      // Parse kids from the name like "2 adults + 0 kids"
      const match = selectedAnswer.name.match(/(\d+) kids?/);
      if (match) {
        const kidsCount = parseInt(match[1], 10);
        return kidsCount;
      }
    }
    const defaultKids = isFamilyMealPlan ? 2 : 0;
    return defaultKids;
  };

  const subheaderText = (() => {
    if (isBoxPeopleStep) {
      return get(BOX_QUESTIONS, [BOX_RECOMMENDER_PATHS.BOX_PEOPLE, `${brand}_subheader`]);
    }
    if (isBoxMealsStep) {
      return get(BOX_QUESTIONS, [BOX_RECOMMENDER_PATHS.BOX_MEALS, `${brand}_subheader`]);
    }
    if (isBoxDaysStep) {
      return get(BOX_QUESTIONS, [BOX_RECOMMENDER_PATHS.BOX_DAYS, 'subheader']);
    }
    return '';
  })();

  return (
    <div className="mx-auto mt-6 w-full px-6 bp1:w-[576px] bp1:px-6">
      <FunnelHeader
        title={
          boxSignupData?.question ? (
            boxSignupData?.question
          ) : (
            <Skeleton count={1} className="my-6" height={16} />
          )
        }
      />
      <Text className="mb-6 mt-3 text-left text-charcoal-100">{subheaderText}</Text>
      {boxSignupData.answers ? (
        isBoxPeopleStep && !isTempoMealPlan ? (
          <AdultsKidsSelector
            onContinue={handleAdultsKidsSelection}
            defaultAdults={getInitialAdults()}
            defaultKids={getInitialKids()}
            isSubmitting={isSubmitting}
            error={error}
          />
        ) : (
          <>
            <BoxQuestionsToggles
              boxSignupData={boxSignupData}
              handleBoxSignupAnswerClick={handleBoxSignupAnswerClick}
              icons={icons}
              pathname={boxRecommenderPath}
            />
            <Button
              className="w-full"
              onClick={handleContinue}
              disabled={isSubmitting}
              variation="Pill"
            >
              Continue
            </Button>
            <CSSTransition
              in={Boolean(error)}
              classNames="fadeInOut"
              timeout={200}
              nodeRef={errorRef}
              unmountOnExit
            >
              <div ref={errorRef}>
                <ErrorMessage message={error} />
              </div>
            </CSSTransition>
          </>
        )
      ) : (
        <>
          <Skeleton count={3} className="my-1" height={32} />
          <Skeleton count={1} className="my-8" height={48} />
        </>
      )}
    </div>
  );
};

export default BoxRecommenderContainer;
